/**
 * MediCore OS Clinical & Ambient Audio Engine
 * Cross-platform audio engine supporting HTML5 Audio, Web Audio API,
 * and SpeechSynthesis fallbacks optimized for Safari, Chrome, and iOS WebKit.
 */

let sharedAudioCtx: AudioContext | null = null;
let currentVoiceAudio: HTMLAudioElement | null = null;
let voiceProgressTimer: any = null;

/**
 * Obtain an active and resumed AudioContext unlocked by user gesture
 */
export const getUnlockedAudioContext = (): AudioContext | null => {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return null;
    if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
      sharedAudioCtx = new AudioCtx();
    }
    if (sharedAudioCtx.state === 'suspended') {
      sharedAudioCtx.resume().catch(() => {});
    }
    return sharedAudioCtx;
  } catch (e) {
    console.warn('AudioContext initialization error:', e);
    return null;
  }
};

/**
 * Play authentic hospital fall emergency alarm chime.
 * Fires both HTML5 audio media and Web Audio synthesizer for 100% audible delivery.
 */
export const playFallAlertSound = () => {
  // 1. Play HTML5 audio file (M4A / WAV)
  try {
    const alarm = new Audio('/assets/audio/fall-alert-alarm.m4a');
    alarm.volume = 1.0;
    const playPromise = alarm.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Fallback to WAV format
        const wavAlarm = new Audio('/assets/audio/fall-alert-alarm.wav');
        wavAlarm.volume = 1.0;
        wavAlarm.play().catch(() => {});
      });
    }
  } catch (e) {
    console.warn('HTML5 Audio fallback error:', e);
  }

  // 2. Play Web Audio synthesizer (loud triangle wave telemetry alert)
  try {
    const ctx = getUnlockedAudioContext();
    if (ctx) {
      const now = ctx.currentTime;
      // 4-tone rapid clinical emergency alert
      const alertTones = [
        { freq: 880, start: 0, dur: 0.16 },
        { freq: 660, start: 0.18, dur: 0.16 },
        { freq: 880, start: 0.36, dur: 0.18 },
        { freq: 1174.66, start: 0.56, dur: 0.32 }
      ];

      alertTones.forEach((t) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle'; // Loud and rich harmonics for laptop/phone speakers
        osc.frequency.setValueAtTime(t.freq, now + t.start);
        gain.gain.setValueAtTime(0.7, now + t.start);
        gain.gain.exponentialRampToValueAtTime(0.001, now + t.start + t.dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + t.start);
        osc.stop(now + t.start + t.dur);
      });
    }
  } catch (e) {
    console.warn('Web Audio alarm chime error:', e);
  }
};

/**
 * Play soft telephone connect/handshake tone
 */
export const playConnectTone = () => {
  try {
    const ctx = getUnlockedAudioContext();
    if (ctx) {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(523.25, now);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.35);
    }
  } catch (e) {}
};

/**
 * Play authentic Voice Bio recording with Eleanor Vance's actual spoken audio.
 * Fallback to browser SpeechSynthesis with full Safari & Chrome compatibility.
 */
export const playVoiceBioAudio = (
  residentName: string,
  onProgress: (seconds: number) => void,
  onEnd: () => void
) => {
  stopVoiceBioAudio();
  playConnectTone();

  let audioStarted = false;

  // 1. Attempt primary playback via high-quality recorded M4A / WAV audio asset
  try {
    const audio = new Audio('/assets/audio/eleanor-wellness-call.m4a');
    audio.volume = 1.0;
    currentVoiceAudio = audio;

    audio.ontimeupdate = () => {
      onProgress(Math.floor(audio.currentTime));
    };

    audio.onended = () => {
      stopVoiceBioAudio();
      onEnd();
    };

    audio.onerror = () => {
      // If M4A fails, try WAV format
      const wavAudio = new Audio('/assets/audio/eleanor-wellness-call.wav');
      wavAudio.volume = 1.0;
      currentVoiceAudio = wavAudio;
      wavAudio.ontimeupdate = () => {
        onProgress(Math.floor(wavAudio.currentTime));
      };
      wavAudio.onended = () => {
        stopVoiceBioAudio();
        onEnd();
      };
      wavAudio.onerror = () => {
        fallbackToSpeechSynthesis(residentName, onProgress, onEnd);
      };
      wavAudio.play().catch(() => {
        fallbackToSpeechSynthesis(residentName, onProgress, onEnd);
      });
    };

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          audioStarted = true;
        })
        .catch(() => {
          // If browser blocked media, fallback to WAV or SpeechSynthesis
          fallbackToSpeechSynthesis(residentName, onProgress, onEnd);
        });
    }
  } catch (e) {
    fallbackToSpeechSynthesis(residentName, onProgress, onEnd);
  }
};

/**
 * Robust SpeechSynthesis fallback with WebKit / Safari garbage collection prevention
 */
const fallbackToSpeechSynthesis = (
  residentName: string,
  onProgress: (seconds: number) => void,
  onEnd: () => void
) => {
  if (!('speechSynthesis' in window)) {
    // If neither is supported, simulate progress timer and finish
    let sec = 0;
    voiceProgressTimer = setInterval(() => {
      sec += 1;
      onProgress(sec);
      if (sec >= 12) {
        stopVoiceBioAudio();
        onEnd();
      }
    }, 1000);
    return;
  }

  try {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    }

    const text = `Good morning, this is ${residentName} in Room 101. I slept pretty well last night, though my left knee is a little stiff. I took my morning medications with breakfast, and I am feeling steady on my feet. Thank you for checking in on me.`;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Pick best available voice
    const voices = window.speechSynthesis.getVoices();
    if (voices && voices.length > 0) {
      const preferred = voices.find(
        (v) =>
          v.lang.startsWith('en') &&
          (v.name.includes('Samantha') || v.name.includes('Karen') || v.name.includes('Natural'))
      ) || voices.find((v) => v.lang.startsWith('en'));
      if (preferred) utterance.voice = preferred;
    }

    // Save to window object to prevent Safari/Chrome garbage-collection mid-utterance
    (window as any)._activeVoiceUtterance = utterance;

    let sec = 0;
    voiceProgressTimer = setInterval(() => {
      sec += 1;
      onProgress(sec);
    }, 1000);

    utterance.onend = () => {
      stopVoiceBioAudio();
      onEnd();
    };

    utterance.onerror = () => {
      stopVoiceBioAudio();
      onEnd();
    };

    window.speechSynthesis.speak(utterance);
  } catch (e) {
    console.warn('SpeechSynthesis error:', e);
    stopVoiceBioAudio();
    onEnd();
  }
};

/**
 * Stop any running voice playback immediately
 */
export const stopVoiceBioAudio = () => {
  if (voiceProgressTimer) {
    clearInterval(voiceProgressTimer);
    voiceProgressTimer = null;
  }

  if (currentVoiceAudio) {
    try {
      currentVoiceAudio.pause();
      currentVoiceAudio.currentTime = 0;
    } catch (e) {}
    currentVoiceAudio = null;
  }

  if ('speechSynthesis' in window) {
    try {
      window.speechSynthesis.cancel();
    } catch (e) {}
  }
  (window as any)._activeVoiceUtterance = null;
};
